// eslint-disable dot-notation
import {serverSideTranslations} from "next-i18next/serverSideTranslations"
import {useRouter} from "next/router"
import PropTypes from "prop-types"
import React, {useEffect} from "react"
import BikeDetails from "../../components/bikeDetails/BikeDetails"
import Footer from "../../components/footer/Footer"
import {
	findBikeMetaFieldByName,
	getAttributeRelations,
	getSimilarProducts,
	parseJsonColorData
} from "../../utility/Helper"
import MbpHead from "../../components/head/MbpHead"
import NavBar from "../../components/navigation/NavBar"
import {Currencies} from "../../config/facebook/events"
import {DEFAULT_ATTRIBUTES_TO_SHOW_IN_FILTERS, defaultParams} from "../../constants/index"

import {buildTimeGetProductQuestions} from "../../firebaseAdmin/buildTimeGetProductQuestions"
import {getDeals} from "../../hubspot/getDeals"
import {getPageData} from "../../pageData"
import {
	buildTimeFetchBikeSlugs,
	buildTimeFetchOccasionBikeSlugs,
	executeSqlQuery,
	getAllBikes,
	getBikesWithOffsetAndLimit
} from "../../postgres/selectHelpers/selectQuery"
import nextI18NextConfig from "../../next-i18next.config.js"
import {filterResultsQuery} from "../../utility/SqlQueryFunctions"
import {BikeType} from "../../utility/Types"
import {checkNoDataSizeVariation} from "../../utility/helpers/checkNoDataSizeVariation"

type Props = {
  bike: any
  reviews: any
	questions: any[]
	slug?:string
}

const Bike: React.FC<Props> = ({bike, questions, slug}) => {
	const router = useRouter()
	const title = bike ? findBikeMetaFieldByName(bike, "seo_title") : null
	const description = bike ? findBikeMetaFieldByName(bike, "seo_description") : null
	const imageUrl = bike ? bike.images?.length > 0 ? bike.images[0].src : null : null
	const brand = bike?.attributes?.find(e => e.slug === "brand")?.terms[0]
	// Redirect if bike no longer exists
	useEffect(() => {
		if (!bike) {
			sessionStorage.setItem("wrongSlug", slug)
			router.replace("/404").then()
		}
	}, [bike])

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [])
	return (bike ?
		<>
			<MbpHead>
				<title>
					{title}
				</title>
				<meta name="description"
					content={description}/>
				<meta property="og:title"
					content={title}/>
				<meta property="og:description"
					content={description}/>
				<meta property="og:image"
					content={imageUrl}/>
				<meta property="product:brand"
					content={brand?.name}/>
				<meta property="product:condition"
					content="new"/>
				<meta property="product:price:amount"
					content={bike.uvpPrice}/>
				<meta property="product:price:currency"
					content={Currencies.CHF}/>
			</MbpHead>
			<div id="btn-onlineSizer" />
			<NavBar/>
			<BikeDetails bike={bike}
				questions={questions}/>
			<Footer/>
		</> : null
	)
}

Bike.propTypes = {
	bike: PropTypes.any
}

export async function getStaticPaths() {
	// Call an external API endpoint to get posts
	const pathsDe = await buildTimeFetchBikeSlugs("de")
	const pathsFr = await buildTimeFetchBikeSlugs("fr")
	const occasionPathsDe = await buildTimeFetchOccasionBikeSlugs("de")
	const occasionPathsFr = await buildTimeFetchOccasionBikeSlugs("fr")

	const paths = pathsDe.concat(pathsFr.filter(path => path?.params?.bike), occasionPathsDe.filter(path => path?.params?.bike), occasionPathsFr.filter(path => path?.params?.bike)).filter(path => path?.params?.bike)

	// We'll pre-render only these paths at build time.
	// { fallback: false } means other routes should 404.
	return {paths, fallback: "blocking"}
}

// CLEAN UP
export const getStaticProps = async ({locale, params}) => {
	let similarBikes: BikeType[] = []
	const result = await getBikesWithOffsetAndLimit(locale, defaultParams, ["attributes", "categories", "images", "cross_sell_products", "meta_data", "static_variations"], DEFAULT_ATTRIBUTES_TO_SHOW_IN_FILTERS, 0, 1, params.bike, null, true)
	const bike = result[0]
	const bikes = await getAllBikes(locale, false)

	similarBikes = bike?.categories ? getSimilarProducts(bike, bikes, 10) || [] : []
	const questions = bike ? await buildTimeGetProductQuestions(bike.slug, locale) : null
	let serializedUpsellsList = []
	const relatedAttributeBatterySizes = getAttributeRelations(bike, bikes, "battery_size", "battery_capacity")
	const relatedMotorStrength = getAttributeRelations(bike, bikes, "motor_strength", "motor")
	const relatedCategoryFrames = getAttributeRelations(bike, bikes, "frame", "frame_variant")
	const pageData = await getPageData(locale)

	if (bike?.stock_status === "outofstock") {
		return {
			props: {
				...pageData,
				bike: bike ? {
					...bike,
					similarBikes: similarBikes || [],
					colorData: bike.colorData || [],
					upsellsList: serializedUpsellsList || [],
					relatedAttributeBatterySizes,
					relatedMotorStrength,
					relatedCategoryFrames
				} : null,
				questions,
				deals: await getDeals(),
				...(await serverSideTranslations(locale, ["common", "customerquestionform", "productDescrPage", "URL", "search", "secondHandBikes", "filters", "sponsorship"], nextI18NextConfig))
			}
		}
	}

	if (bike?.upsell_ids) {
		const getUpsellsByidsSqlQuery = filterResultsQuery([], defaultParams, ["categories", "images", "meta_data", "variation_objects"], null, null, locale, null, null, null, bike.upsell_ids)
		const upsellsResult = await executeSqlQuery(getUpsellsByidsSqlQuery)
		serializedUpsellsList = Array.isArray(upsellsResult) ? upsellsResult?.map(bike => ({
			...bike,
			colorData: parseJsonColorData(bike.color_data),
			// eslint-disable-next-line
			date_created: bike["date_created"].toString()
		})) : null
	}

	await checkNoDataSizeVariation(bike)

	return {
		props: {
			...pageData,
			bike: bike ? {
				...bike,
				similarBikes: similarBikes || [],
				colorData: bike.colorData || [],
				upsellsList: serializedUpsellsList || [],
				relatedAttributeBatterySizes,
				relatedMotorStrength,
				relatedCategoryFrames
			} : null,
			questions,
			deals: await getDeals(),
			slug: params.bike,
			...(await serverSideTranslations(locale, ["common", "customerquestionform", "productDescrPage", "URL", "search", "secondHandBikes", "filters", "sponsorship"], nextI18NextConfig))
		}
	}
}

export default Bike
